import React, { useCallback, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useCatalogData } from '../CatalogData/CatalogDataProvider';
import { resolveCategoryIcon } from '../../config/categoryIcons';
import './CategoriesPage.css';

const normalizeId = value => String(value ?? '').trim();

const getNumericOrder = value => {
    if (value === null || value === undefined || String(value).trim() === '') {
        return null;
    }

    const numericOrder = Number(value);
    return Number.isFinite(numericOrder) ? numericOrder : null;
};

export const getVisibleCategories = (categories, products) => {
    const productCategoryIds = new Set(
        products
            .map(product => normalizeId(product?.idCategoria))
            .filter(Boolean)
    );

    return categories
        .map((category, receivedIndex) => ({
            receivedIndex,
            id: normalizeId(category?.idCategoria),
            name: String(category?.categoria ?? '').trim(),
            image: typeof category?.imagen === 'string'
                ? category.imagen.trim()
                : '',
            numericOrder: getNumericOrder(category?.orden),
        }))
        .filter(item => (
            item.id
            && item.name
            && productCategoryIds.has(item.id)
        ))
        .sort((firstCategory, secondCategory) => {
            const firstHasOrder = firstCategory.numericOrder !== null;
            const secondHasOrder = secondCategory.numericOrder !== null;

            if (firstHasOrder && secondHasOrder) {
                return firstCategory.numericOrder - secondCategory.numericOrder
                    || firstCategory.receivedIndex - secondCategory.receivedIndex;
            }

            if (firstHasOrder) {
                return -1;
            }

            if (secondHasOrder) {
                return 1;
            }

            return firstCategory.receivedIndex - secondCategory.receivedIndex;
        });
};

export default function CategoriesPage() {
    const {
        productos,
        categorias,
        status,
        errors,
        ensureProductos,
        ensureCategorias,
        retryProductos,
        retryCategorias,
    } = useCatalogData();

    useEffect(() => {
        if (status.productos === 'idle') {
            ensureProductos().catch(() => {});
        }

        if (status.categorias === 'idle') {
            ensureCategorias().catch(() => {});
        }
    }, [
        ensureCategorias,
        ensureProductos,
        status.categorias,
        status.productos,
    ]);

    const visibleCategories = useMemo(
        () => getVisibleCategories(categorias, productos),
        [categorias, productos]
    );
    const loading = (
        status.productos === 'idle'
        || status.productos === 'loading'
        || status.categorias === 'idle'
        || status.categorias === 'loading'
    );
    const hasError = (
        status.productos === 'error'
        || status.categorias === 'error'
        || Boolean(errors.productos)
        || Boolean(errors.categorias)
    );

    const handleRetry = useCallback(() => {
        const pendingRequests = [];

        if (status.productos === 'error') {
            pendingRequests.push(retryProductos());
        } else if (status.productos === 'idle') {
            pendingRequests.push(ensureProductos());
        }

        if (status.categorias === 'error') {
            pendingRequests.push(retryCategorias());
        } else if (status.categorias === 'idle') {
            pendingRequests.push(ensureCategorias());
        }

        pendingRequests.forEach(request => request.catch(() => {}));
    }, [
        ensureCategorias,
        ensureProductos,
        retryCategorias,
        retryProductos,
        status.categorias,
        status.productos,
    ]);

    return (
        <section
            className='categoriesView'
            aria-labelledby='categories-page-title'
            aria-busy={loading && !hasError}
        >
            {hasError ? (
                <div className='categoriesView__feedback' role='alert'>
                    <h2>No pudimos cargar las categorías.</h2>
                    <p>Revisa tu conexión e inténtalo nuevamente.</p>
                    <button type='button' onClick={handleRetry}>
                        Reintentar
                    </button>
                </div>
            ) : loading ? (
                <div
                    className='categoriesView__grid categoriesView__grid--loading'
                    role='status'
                    aria-label='Cargando categorías'
                >
                    {Array.from({ length: 6 }, (_, index) => (
                        <span
                            className='categoriesView__skeleton'
                            aria-hidden='true'
                            key={index}
                        />
                    ))}
                </div>
            ) : visibleCategories.length === 0 ? (
                <div className='categoriesView__feedback' role='status'>
                    <h2>No hay categorías disponibles por ahora.</h2>
                    <p>Vuelve pronto para descubrir nuevos productos.</p>
                </div>
            ) : (
                <ul className='categoriesView__grid'>
                    {visibleCategories.map(({ id, name, image }) => (
                        <li key={id}>
                            <Link
                                className='categoriesView__card'
                                to={`/productos?categoria=${encodeURIComponent(id)}`}
                            >
                                <img
                                    className={`categoriesView__image ${
                                        image
                                            ? 'categoriesView__image--photo'
                                            : 'categoriesView__image--fallback'
                                    }`}
                                    src={image || resolveCategoryIcon(name)}
                                    alt=''
                                    aria-hidden='true'
                                    loading="lazy"
                                    decoding="async"
                                />
                                <span>{name}</span>
                            </Link>
                        </li>
                    ))}
                </ul>
            )}
        </section>
    );
}
